// @ts-nocheck
/* ------------------------------------------------------------------
   SALT — Pinned Scroll Hero
   ------------------------------------------------------------------
   One pinned section, scrubbed through 4 stages:
     1) resting video + heading/button
     2) video frame crops down
     3) paragraph reveal
     4) GLB enters
     5) feature lines reveal line by line

   The three.js GLB block below is intentionally the same solution as
   assets/salt-scroll-story.js (same loaders, same camera-framing math,
   same tilt/spin pivots) — only its *visibility* is now scroll-tied.
   ------------------------------------------------------------------ */

(function () {
  "use strict";

  var THREE_URL = "https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.min.js";
  var GLTF_LOADER_URL = "https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js";
  var DRACO_LOADER_URL = "https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/DRACOLoader.js";
  var DRACO_DECODER_PATH = "https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/libs/draco/gltf/";

  var GSAP_WAIT_INTERVAL = 50;
  var GSAP_WAIT_TIMEOUT = 10000;

  var scriptPromises = {};
  function loadScript(src) {
    if (scriptPromises[src]) return scriptPromises[src];
    scriptPromises[src] = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        existing.addEventListener("load", resolve);
        existing.addEventListener("error", reject);
        if (existing.getAttribute("data-loaded") === "true") resolve();
        return;
      }
      var s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = function () {
        s.setAttribute("data-loaded", "true");
        resolve();
      };
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return scriptPromises[src];
  }

  function loadThreeDependencies() {
    var threeChain = window.THREE ? Promise.resolve() : loadScript(THREE_URL);
    return threeChain
      .then(function () {
        var loaders = [];
        if (!window.THREE.GLTFLoader) loaders.push(loadScript(GLTF_LOADER_URL));
        if (!window.THREE.DRACOLoader) loaders.push(loadScript(DRACO_LOADER_URL));
        return Promise.all(loaders);
      });
  }

  function waitForGsap() {
    return new Promise(function (resolve, reject) {
      var waited = 0;
      (function poll() {
        if (window.gsap && window.ScrollTrigger) {
          resolve();
          return;
        }
        waited += GSAP_WAIT_INTERVAL;
        if (waited >= GSAP_WAIT_TIMEOUT) {
          reject(new Error("SALT Hero: gsap/ScrollTrigger never became available"));
          return;
        }
        setTimeout(poll, GSAP_WAIT_INTERVAL);
      })();
    });
  }

  // Waits for the page's own "load" event (all images/video/fonts finished
  // fetching) rather than just DOMContentLoaded — ScrollTrigger measurements
  // taken before that can be wrong if a video poster or web font hasn't
  // resolved yet and later shifts layout.
  function waitForPageReady() {
    var loadPromise =
      document.readyState === "complete"
        ? Promise.resolve()
        : new Promise(function (resolve) {
            window.addEventListener("load", resolve, { once: true });
          });
    var fontsPromise =
      document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    return Promise.all([loadPromise, fontsPromise]);
  }

  function createEnvironmentTexture() {
    var envCanvas = document.createElement("canvas");
    envCanvas.width = 4;
    envCanvas.height = 256;
    var ctx = envCanvas.getContext("2d");
    var gradient = ctx.createLinearGradient(0, 0, 0, envCanvas.height);
    gradient.addColorStop(0, "#fdfaf4");
    gradient.addColorStop(0.45, "#cfc9be");
    gradient.addColorStop(1, "#3a362f");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, envCanvas.width, envCanvas.height);

    var texture = new THREE.CanvasTexture(envCanvas);
    texture.mapping = THREE.EquirectangularReflectionMapping;
    texture.encoding = THREE.sRGBEncoding;
    return texture;
  }

  /* ---------- three.js GLB scene — same solution as salt-scroll-story.js ---------- */
  function initGlbScene(root, config, reducedMotion, onFirstRender) {
    var canvasWrap = root.querySelector("[data-salt-canvas-wrap]");
    var canvas = root.querySelector("[data-salt-canvas]");
    if (!canvasWrap || !canvas) return function cleanup() {};

    var renderer, scene, camera, model;
    var ambient, hemisphere, key, rim;
    var modelHalfHeight;
    var modelHorizontalRadius;
    var tiltPivot, spinPivot;
    var animationFrameId;
    var rotationLastTime;
    var rotationSpeed = 0.6;
    var resizeObserver;

    function configNumber(name, fallback) {
      var value = Number(config[name]);
      return config[name] === undefined || config[name] === null || !isFinite(value)
        ? fallback
        : value;
    }

    function renderScene() {
      if (renderer && scene && camera) renderer.render(scene, camera);
    }

    function updateCameraFraming() {
      if (!camera || !modelHalfHeight || !modelHorizontalRadius) return;

      var fovRad = THREE.MathUtils.degToRad(camera.fov);
      var effectiveHalfFovV = Math.atan(Math.tan(fovRad / 2) / camera.zoom);
      var effectiveHalfFovH = Math.atan(Math.tan(effectiveHalfFovV) * camera.aspect);
      var distV = modelHorizontalRadius + modelHalfHeight / Math.tan(effectiveHalfFovV);
      var distH = modelHorizontalRadius / Math.sin(effectiveHalfFovH);
      var exactSafeDistance = Math.max(distV, distH);
      var trueFloor = exactSafeDistance * 1.02;
      var baselineDistance = exactSafeDistance * 1.1 * 1.15;
      var sizePercent = configNumber("modelSize", 100);
      var distance = Math.max(baselineDistance / (sizePercent / 100), trueFloor);

      camera.position.set(0, 0, distance);
      camera.near = Math.max(distance / 100, 0.01);
      camera.far = distance * 10;
      camera.updateProjectionMatrix();
    }

    function animateRotation(time) {
      if (!spinPivot || reducedMotion) {
        animationFrameId = undefined;
        return;
      }
      if (rotationLastTime === undefined) rotationLastTime = time;
      var delta = Math.min((time - rotationLastTime) / 1000, 0.1);
      rotationLastTime = time;
      spinPivot.rotation.y += delta * rotationSpeed;
      renderScene();
      animationFrameId = requestAnimationFrame(animateRotation);
    }

    function resizeRenderer() {
      if (!renderer || !canvasWrap) return;
      var rect = canvasWrap.getBoundingClientRect();
      var w = rect.width || 1;
      var h = rect.height || 1;
      renderer.setSize(w, h, false);
      if (camera) {
        camera.aspect = w / h;
        updateCameraFraming();
        camera.updateProjectionMatrix();
      }
      renderScene();
    }


    renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.65;

    scene = new THREE.Scene();
    scene.environment = createEnvironmentTexture();

    camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
    camera.position.set(0, 0, 6);
    camera.zoom = 1.75;

    ambient = new THREE.AmbientLight(0xffffff, 2.1);
    scene.add(ambient);

    hemisphere = new THREE.HemisphereLight(0xffffff, 0x2b2b2b, 0.8);
    scene.add(hemisphere);

    key = new THREE.DirectionalLight(0xffffff, 0.7);
    key.position.set(0.8, 2.4, 4.9);
    scene.add(key);

    rim = new THREE.DirectionalLight(0xffffff, 2.5);
    rim.position.set(-4.2, -1, 0.4);
    scene.add(rim);


    resizeRenderer();

    var loader = new THREE.GLTFLoader();
    if (window.THREE.DRACOLoader) {
      var dracoLoader = new THREE.DRACOLoader();
      dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
      dracoLoader.setDecoderConfig({ type: "js" });
      loader.setDRACOLoader(dracoLoader);
    }

    loader.load(
      config.modelUrl,
      function (gltf) {
        model = gltf.scene;

        var box = new THREE.Box3().setFromObject(model);
        var center = new THREE.Vector3();
        box.getCenter(center);
        model.position.sub(center);

        tiltPivot = new THREE.Group();
        tiltPivot.rotation.set(
          THREE.MathUtils.degToRad(configNumber("tiltX", -16)),
          THREE.MathUtils.degToRad(configNumber("tiltY", 49)),
          THREE.MathUtils.degToRad(configNumber("tiltZ", 22))
        );
        tiltPivot.add(model);

        spinPivot = new THREE.Group();
        spinPivot.rotation.y = THREE.MathUtils.degToRad(configNumber("hoverRotationStart", 0));
        spinPivot.add(tiltPivot);

        box.setFromObject(spinPivot);
        var fittedSize = new THREE.Vector3();
        box.getSize(fittedSize);
        modelHalfHeight = fittedSize.y / 2 || 1;
        modelHorizontalRadius = Math.hypot(fittedSize.x, fittedSize.z) / 2 || 1;
        updateCameraFraming();

        scene.add(spinPivot);
        renderScene();
        if (!reducedMotion && animationFrameId === undefined) {
          rotationLastTime = undefined;
          animationFrameId = requestAnimationFrame(animateRotation);
        }
        if (typeof onFirstRender === "function") onFirstRender();
      },
      undefined,
      function (err) {
        // eslint-disable-next-line no-console
        console.error("SALT Hero: could not load 3D model at", config.modelUrl, err);
        if (canvasWrap) canvasWrap.style.display = "none";
      }
    );

    resizeObserver = new ResizeObserver(function () {
      resizeRenderer();
    });
    resizeObserver.observe(canvasWrap);

    return function cleanup() {
      if (animationFrameId !== undefined) cancelAnimationFrame(animationFrameId);
      if (resizeObserver) resizeObserver.disconnect();
      if (renderer) renderer.dispose();
    };
  }

  function splitIntoChars(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    var textNodes = [];
    var node;
    while ((node = walker.nextNode())) textNodes.push(node);

    var chars = [];
    textNodes.forEach(function (textNode) {
      var text = textNode.textContent;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < text.length; i++) {
        var ch = text[i];
        if (/\s/.test(ch)) {
          frag.appendChild(document.createTextNode(ch));
        } else {
          var span = document.createElement("span");
          span.className = "salt-hero__char";
          span.style.opacity = "0";
          span.textContent = ch;
          frag.appendChild(span);
          chars.push(span);
        }
      }
      textNode.parentNode.replaceChild(frag, textNode);
    });
    return chars;
  }

  /* ---------- pinned scroll timeline ---------- */
  function initSection(root) {
    var sectionId = root.getAttribute("data-section-id");
    var config = (window.saltHeroConfig && window.saltHeroConfig[sectionId]) || {};
    var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    var track = root.querySelector("[data-salt-hero-track]");
    var pin = root.querySelector("[data-salt-hero-pin]");
    var videoFrame = root.querySelector("[data-salt-hero-video-frame]");
    var paragraph = root.querySelector("[data-salt-hero-paragraph]");
    var canvasWrap = root.querySelector("[data-salt-canvas-wrap]");
    var features = Array.prototype.slice.call(root.querySelectorAll("[data-salt-hero-feature]"));

    function refreshScrollTrigger() {
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }

    var glbCleanup = initGlbScene(root, config, reducedMotion, refreshScrollTrigger);

    if (reducedMotion || !window.gsap || !window.ScrollTrigger) {
      // Fallback: no pin, no scrub — just show the final composed state.
      if (videoFrame) videoFrame.style.opacity = 0;
      if (paragraph) paragraph.style.opacity = 0;
      if (canvasWrap) {
        canvasWrap.style.opacity = 1;
        canvasWrap.style.transform = "scale(1)";
      }
      features.forEach(function (el) {
        el.style.opacity = 1;
        el.style.transform = "translateY(0)";
      });
      return function cleanup() {
        glbCleanup();
      };
    }

    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    var isAndroid = /Android/i.test(navigator.userAgent);
    gsap.registerPlugin(ScrollTrigger);

    // Android Chrome changes the visual viewport height as its address bar
    // expands and collapses. Treating those height-only changes as full
    // resizes can refresh an active pin halfway through reverse scrolling and
    // briefly expose the video frame's light fallback background.
    if (window.matchMedia("(max-width: 989px)").matches) {
      ScrollTrigger.config({ ignoreMobileResize: true });
    }

    // This theme scrolls `.page-wrapper` natively on desktop (>=990px) — the
    // document itself is overflow:hidden there. Same pattern as
    // hero-scroll-animation.js's `scroller`. On mobile the document scrolls
    // normally, so the ScrollTrigger default (window) is correct.
    var scroller = window.matchMedia("(min-width: 990px)").matches
      ? document.querySelector(".page-wrapper") || window
      : window;

    // Bridge Lenis (window.themeLenis) and ScrollTrigger so they never
    // compute two different "truths" for scroll position: tell
    // ScrollTrigger to update on every Lenis scroll tick, in addition to
    // whatever native scroll events it already listens to. This is
    // additive only — it does NOT drive Lenis's own rAF loop (that's
    // already owned by the theme elsewhere), so it can't double-drive it.
    var lenisCleanup = (function bridgeLenis() {
      var lenis = window.themeLenis;
      if (!lenis || typeof lenis.on !== "function") return function noop() {};
      var onLenisScroll = function () {
        ScrollTrigger.update();
      };
      lenis.on("scroll", onLenisScroll);
      return function cleanup() {
        if (typeof lenis.off === "function") lenis.off("scroll", onLenisScroll);
      };
    })();

    // One pinned frame, one scrubbed timeline, four crossfading stages:
    //   1) video visible (resting state)
    //   2) video fades out -> paragraph fades in, letter by letter
    //   3) paragraph fades out -> GLB fades/scales in
    //   4) feature rows reveal one by one (GLB stays on top)
    var tl = gsap.timeline({
      defaults: { ease: "none" },
      scrollTrigger: {
        trigger: track,
        scroller: scroller,
        pin: pin,
        pinReparent: isAndroid,
        scrub: 1,
        anticipatePin: 1,
        start: "top top",
        end: "bottom bottom",
      },
    });

    // Built with a running cursor rather than hardcoded positions, because
    // the paragraph's letter-reveal duration depends on how much text is in
    // it (stagger * char count) — a fixed number here previously cut the
    // reveal off mid-way for longer text.
    var CHAR_STAGGER = 0.03;
    var CHAR_TWEEN_DURATION = 0.5;
    var FADE_DURATION = 0.6;
    var GAP = 0.25;
    var HOLD_AFTER_PARAGRAPH = 1.4; // pause once all letters have appeared
    var HOLD_AFTER_ROWS = 1.4; // pause once the last row has revealed

    var cursor = 0;

    // Stage 1 -> 2: video fades out.
    tl.to(videoFrame, { opacity: 0, duration: FADE_DURATION }, cursor);
    cursor += FADE_DURATION + GAP;

    // Stage 2: paragraph reveals letter by letter.
    var chars = paragraph ? splitIntoChars(paragraph) : [];
    if (chars.length) {
      tl.set(paragraph, { opacity: 1 }, cursor);
      tl.to(chars, { opacity: 1, stagger: CHAR_STAGGER }, cursor);
      cursor += CHAR_TWEEN_DURATION + CHAR_STAGGER * (chars.length - 1);
    }

    // Hold on the fully-revealed paragraph before it starts fading.
    cursor += HOLD_AFTER_PARAGRAPH;

    // Stage 2 -> 3: paragraph fades out, GLB fades/scales in.
    if (paragraph) {
      tl.to(paragraph, { opacity: 0, duration: FADE_DURATION }, cursor);
    }
    cursor += FADE_DURATION + GAP;
    tl.fromTo(
      canvasWrap,
      { opacity: 0, scale: 0.85 },
      { opacity: 1, scale: 1, duration: FADE_DURATION },
      cursor
    );
    cursor += FADE_DURATION + GAP;

    // Stage 3 -> 4: feature rows reveal one by one.
    var ROW_DURATION = 0.5;
    var ROW_STAGGER_GAP = 0.3;
    features.forEach(function (el, i) {
      tl.fromTo(
        el,
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: ROW_DURATION },
        cursor
      );
      cursor += i === features.length - 1 ? ROW_DURATION : ROW_STAGGER_GAP;
    });

    // Hold on the fully-revealed rows before releasing the pin.
    cursor += HOLD_AFTER_ROWS;

    // Extend the timeline to the final cursor position explicitly, so the
    // trailing hold is actually part of the scrollable/pinned duration.
    tl.to({}, { duration: 0.01 }, cursor);

    // Pin the timeline immediately so the section is interactive right
    // away, but the FIRST measurement can still be wrong if a video poster
    // or web font hasn't resolved yet and later shifts layout — refresh
    // again once the page's own "load" event and fonts.ready both confirm
    // everything has actually settled (not just DOMContentLoaded).
    refreshScrollTrigger();
    waitForPageReady().then(refreshScrollTrigger);

    var resizeTimeout;
    var lastViewportWidth = window.innerWidth;
    function onResize() {
      var currentViewportWidth = window.innerWidth;
      var isMobileHeightOnlyResize =
        window.matchMedia("(max-width: 989px)").matches &&
        Math.abs(currentViewportWidth - lastViewportWidth) < 2;
      lastViewportWidth = currentViewportWidth;

      if (isMobileHeightOnlyResize) return;

      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(refreshScrollTrigger, 150);
    }
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    return function cleanup() {
      tl.scrollTrigger && tl.scrollTrigger.kill();
      tl.kill();
      glbCleanup();
      lenisCleanup();
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }

  function boot() {
    var sections = document.querySelectorAll(".salt-hero");
    if (!sections.length) return;

    var cleanups = {};

    function bootSection(root) {
      var id = root.getAttribute("data-section-id");

      function afterDeps() {
        cleanups[id] = initSection(root);
      }

      Promise.all([loadThreeDependencies(), waitForGsap()])
        .then(afterDeps)
        .catch(function (err) {
          // eslint-disable-next-line no-console
          console.error("SALT Hero: failed to initialize", err);
        });
    }

    sections.forEach(bootSection);

    document.addEventListener("shopify:section:unload", function (evt) {
      var id = evt.detail && evt.detail.sectionId;
      if (id && cleanups[id]) {
        cleanups[id]();
        delete cleanups[id];
      }
    });

    document.addEventListener("shopify:section:load", function (evt) {
      var root = evt.target && evt.target.querySelector ? evt.target.querySelector(".salt-hero") : null;
      if (root) bootSection(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
